<template>
	<main class="patients-main">
		<div class="flex flex-col">
			<section class="main-banner">
				<div class="banner-text">
					Patients' FAQs
				</div>
			</section>
			<section class="grid grid-cols-1">
				<div class="col-span-1">
					<nav>
						<ul class="page-nav">
							<li>
								<a href="#who-can-sign-up">Who can sign up?</a>
							</li>
							<li>
								<a href="#what-happens-study">What happens during the study?</a>
							</li>
							<li>
								<a href="#how-to-sign-up">How do I sign up?</a>
							</li>
							<li>
								<a href="#contact-for-help">Who can I contact if I need help?</a>
							</li>
							<li>
								<a href="#why-participate">Why should I participate?</a>
							</li>
						</ul>
					</nav>
				</div>
			</section>
			<section class="mt-8 md:mt-10 pb-8 md:pb-10 border-b border-black-blue border-solid" id="who-can-sign-up">
				<column-content-icon>
					<template v-slot:header>
						Who can sign up?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'users']"/>
					</template>
					<template v-slot:content>
						<div class="space-y-4">
							<div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-4 items-center justify-items-center space-y-4 text-center md:text-left">
								<img :src="icon1" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">Patients aged <strong>19 years or older</strong></p>
								<img :src="icon2" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">with chronic pain, epilepsy or issues with sleep, anxiety or depression</p>
								<img :src="icon3" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">and a valid medical document provided by a health care provider.</p>
							</div>
						</div>
					</template>
				</column-content-icon>
			</section>
			<section class="my-8 md:my-10" id="what-happens-study">
				<column-content-icon>
					<template v-slot:header>
						What happens during the study?
					</template>
					<template v-slot:content>
						<div class="space-y-4">
							<div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-4 items-center justify-items-center space-y-4 text-center md:text-left">
								<img :src="icon4" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">The goal is to understand how different strains of medical cannabis help with different symptoms.</p>
								<img :src="icon5" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">Patients use verified medical cannabis products available on MyMedi.ca</p>
								<img :src="icon6" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="ccol-span-1 md:col-span-4 lg:col-span-3">and track changes in symptoms over 6 months.</p>
								<img :src="icon7" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">Researchers monitor patients’ progress but don’t interfere in the treatment, known as an “observational” study.</p>
							</div>
						</div>
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'dna']"/>
					</template>
				</column-content-icon>
			</section>
			<section>
				<callout-block bgColor="#869e33">
					<h2>Talk to your doctor or health care provider about enrolling in the MC RWE study</h2>
				</callout-block>
			</section>
			<section class="my-8 md:my-10" id="how-to-sign-up">
				<column-content-icon class="pb-4">
					<template v-slot:header>
						How do I sign up?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'user-plus']"/>
					</template>
				</column-content-icon>
				<div class="diagram-patient">
					<!-- <div class="diagram-accent"></div> -->
					<patient-diagram-step :icon="icon8" text="Talk to your doctor or health care provider about risks and benefits of the study" bgColor="#292637" class="pb-1"/>
					<patient-diagram-step :icon="icon9" text="and give consent to participate." bgColor="#8bcddb" class="py-1" />
					<patient-diagram-step :icon="icon10" text="Complete baseline questionnaires online" bgColor="#67bed0" class="py-1"/>
					<patient-diagram-step :icon="icon11" text="and use a secure, online platform to order medical cannabis." bgColor="#4fb3c9" class="py-1"/>
					<patient-diagram-step :icon="icon12" text="Then, complete 3 or 4 more online questionnaires to track symptoms over time based on your symptoms." bgColor="#36a7c1" class="py-1"/>
					<patient-diagram-step :icon="icon13" text="We’ll send you reminder emails at 6, 12, and 24 weeks and at 18 weeks if applicable." bgColor="#0093b2" class="pt-1" />
				</div>
			</section>
			<section>
				<callout-block bgColor="#869e33">
					<h2>Talk to your doctor or health care provider <strong>before</strong> you register on the medical cannabis platform!</h2>
				</callout-block>
			</section>
			<section class="mt-8 pb-8 border-b border-black-blue border-solid" id="contact-for-help">
				<column-content-icon>
					<template v-slot:header>
						Who can I contact if I need help?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'phone']"/>
					</template>
					<template v-slot:content>
						<ul class="list-disc list-inside">
						<li>If you have questions about the study, contact the study coordinator at <strong>416-340-4800 x4251</strong> or by email at <a href="mailto:mcrwe@uhn.ca" class="text-olive-green-alt font-bold">mcrwe@uhn.ca</a></li>
						<li>If you have questions about your medical cannabis, call a pharmacist from the MyMedi Patient Support Team <br class="hidden xl:block"/> at <strong>1-844-500-2040 (toll-free)</strong></li>
						</ul>
					</template>
				</column-content-icon>
			</section>
			<section class="my-8 md:my-10" id="why-participate">
				<column-content-icon>
					<template v-slot:header>
						Why should I participate?
					</template>
					<template v-slot:icon>
						<font-awesome-icon :icon="['fas', 'vial']"/>
					</template>
					<template v-slot:content>
						<div class="space-y-4">
							<div class="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-4 items-center justify-items-center space-y-4 text-center md:text-left">
								<img :src="icon14" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									Your participation will help to advance the science of medical cannabis!
								</p>
								<img :src="icon15" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									You will be using high-quality medical cannabis that has been verified to determine its exact contents.
								</p>
								<img :src="icon16" class="h-16 mx-auto col-span-1 md:col-span-2 lg:col-span-1" />
								<p class="col-span-1 md:col-span-4 lg:col-span-3">
									You will receive $5 <strong>for completed questionnaires at various study time points (i.e., baseline, 6, 12, 18 and 24 weeks) for a maximum of $20 at the end of your study participation</strong>, and a discount of 20% on your medical cannabis products for the duration of the study.
								</p>
							</div>
						</div>
					</template>
				</column-content-icon>
			</section>
		</div>
	</main>
</template>

<script>
import PatientDiagramStep from '../components/PatientDiagramStep'
import Callout from '../components/Callout'
import ColumnContentIcon from '../components/ColumnContentIcon'

import icon1 from '../assets/icons/patients/patients_sign-up_1.png'
import icon2 from '../assets/icons/patients/patients_sign-up_2.png'
import icon3 from '../assets/icons/patients/patients_sign-up_3.png'

import icon4 from '../assets/icons/patients/patients_what-happens_1.png'
import icon5 from '../assets/icons/patients/patients_what-happens_2.png'
import icon6 from '../assets/icons/patients/patients_what-happens_3.png'
import icon7 from '../assets/icons/patients/patients_what-happens_4.png'

import icon8 from '../assets/icons/patients/patients_how-sign-up_1.png'
import icon9 from '../assets/icons/patients/patients_how-sign-up_2.png'
import icon10 from '../assets/icons/patients/patients_how-sign-up_3.png'
import icon11 from '../assets/icons/patients/patients_how-sign-up_4.png'
import icon12 from '../assets/icons/patients/patients_how-sign-up_5.png'
import icon13 from '../assets/icons/patients/patients_how-sign-up_6.png'

import icon14 from '../assets/icons/patients/patients_participate_1.png'
import icon15 from '../assets/icons/patients/patients_participate_2.png'
import icon16 from '../assets/icons/patients/patients_participate_3.png'

export default {
	name: 'patient-page',
	components: {
		'patient-diagram-step': PatientDiagramStep,
		'callout-block': Callout,
		'column-content-icon': ColumnContentIcon
	},
	data() {
		return {
			icon1,
			icon2,
			icon3,
			icon4,
			icon5,
			icon6,
			icon7,
			icon8,
			icon9,
			icon10,
			icon11,
			icon12,
			icon13,
			icon14,
			icon15,
			icon16
		}
	}
}
</script>

<style lang="postcss" scoped>
	.patients-main {
		background-color: #fff;
	}
	.main-banner {
		width: 100%;
		height: 125px;
		display: flex;
		align-items: center;
		background-image:
		linear-gradient(to top right, rgba(134, 158, 51, 0.5), rgba(38, 156, 189, 0.75)),
		url('../assets/images/1x/patients-banner.jpg');
		background-position: center 30%;
		background-size: cover;
		background-repeat: no-repeat;
		.banner-text {
			width: 100%;
			margin: 0 2rem;
			padding: 0 0 2rem 0;
			font-size: 2rem;
			color: #fff;
			text-align: left;
			border-bottom: 3px solid #fff;
		}
	}
	.page-nav {
		width: 100%;
		padding: 1.5rem 2rem;
		background-color: theme('colors.black-blue');
		color: theme('colors.olive-green-alt');
		font-family: theme('fontFamily.arial-bold');
		li {
			padding: 0.5rem;
			a {
				display: inline-block;
				width: 100%;
				font-size: 1.125rem;
				text-align: left;
				transition: all 0.5s;
			}
			a:hover {
				color: #fff;
			}
		}
	}
	.diagram-patient {
		display: flex;
		flex-direction: column;
		position: relative;
		padding: 0 2rem;
	}
	.diagram-accent {
		width: 10px;
		height: 100%;
		position: absolute;
		left: calc(2rem + 40px);
		background-color: #ccc;
		z-index: 0;
	}
	@media screen and (min-width: 640px) {
		.main-banner {
			height: 175px;
			.banner-text {
				margin: 0 6rem;
			}
		}
		.page-nav {
			padding: 2rem 0;
			li {
				padding: 0.75rem 6rem;
				a {
					font-size: 1.825rem;
				}
			}
		}
		.diagram-patient {
			padding: 0 6rem;
		}
		.diagram-accent {
			left: calc(6rem + 40px);
		}
	}
	@media screen and (min-width: 769px) {}
</style>
